import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, Chip, Skeleton, Stack, Typography} from 'views/components';
import {PersonOutline} from 'views/icons';
import {Translate, translate} from 'views/module';

import RecentInvocations from './components/RecentInvocations';

import getAutomationRules from '../util/automationRules';
import {isCustomAutomation} from '../util/automationHelper';

const AutomationsView = (props) => {
  const {
    onDelete,
    onEdit,
    automation,
    assigneesLoading,
    assignees,
    automationInvocations,
    automationInvocationsLoading,
  } = props;

  const isAutomationSelected = Boolean(automation);
  const resourceType = automation?.resourceType || null;
  const updateRules = automation?.updateRules || [];
  const descriptionBlob = automation?.description?.blob || '';
  const prompt = automation?.prompt || '';
  const assigneeRns = automation?.assigneeRns || [];

  const automationRules = React.useMemo(() => getAutomationRules(), []);

  const isTriggerLocked = React.useMemo(() => {
    if (!isAutomationSelected) {
      return false;
    }

    return isCustomAutomation(automationRules, resourceType, updateRules);
  }, [isAutomationSelected, resourceType, updateRules, automationRules]);

  const automationRule = React.useMemo(() => {
    return automationRules.find((rule) => rule.resourceType === resourceType) || automationRules[0];
  }, [resourceType, automationRules]);

  const triggerConditionLines = React.useMemo(() => {
    if (!isAutomationSelected || !automationRule?.fields) {
      return [];
    }

    const eventResourcePrepend = translate(
        'automations.Automations.New.Steps.Rules.TriggerConditions.EventResourcePrepend',
    );
    const aiPromptLabel = translate('automations.Automations.New.Steps.Rules.AiEvaluatedRuleTitle');
    const stringEqualLines = [];
    const aiPromptLines = [];

    updateRules.forEach((updateRule) => {
      if (updateRule.ruleType === 'AI_PROMPT') {
        const promptValue = updateRule.promptCheck.prompt;

        if (promptValue) {
          aiPromptLines.push(`${aiPromptLabel}: ${promptValue}`);
        }

        return;
      }

      if (updateRule.ruleType !== 'STRING_EQUAL') {
        return;
      }

      const matchedField = automationRule.fields.find((field) => {
        return field.ruleType === 'STRING_EQUAL' && updateRule.stringEqualCheck.key === field.key;
      });

      if (!matchedField || !matchedField.isVisibleToAdmin) {
        return;
      }

      const matchedValue = matchedField.options.find((option) => {
        return option.value === updateRule.stringEqualCheck.value;
      });

      if (!matchedValue) {
        return;
      }

      stringEqualLines.push(
          `${eventResourcePrepend} ${automationRule.resourceTypeLabel} ${automationRule.sentenceConnector} ${matchedValue.label}`,
      );
    });

    return [...stringEqualLines, ...aiPromptLines];
  }, [isAutomationSelected, updateRules, automationRule]);

  if (!isAutomationSelected) {
    return (
      <Box
        sx={{
          width: '100%',
          p: '40px',
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#A8A49E',
            lineHeight: '150%',
          }}
        >
          <Translate expression='automations.Automations.View.Empty' />
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        p: '40px',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          gap: '16px',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <Stack
            sx={{
              gap: '12px',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              sx={{
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'Regular',
                  fontSize: '22px',
                  color: '#1A1916',
                  lineHeight: '100%',
                }}
              >
                {automation.title}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontWeight: 400,
                fontStyle: 'Regular',
                fontSize: '14px',
                color: '#A8A49E',
                lineHeight: '100%',
              }}
            >
              {automation.groupName}
            </Typography>
          </Stack>
          <Stack
            direction='row'
            sx={{
              gap: '8px',
            }}
          >
            <Button
              onClick={onEdit}
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#FFFFFF',
                'backgroundColor': '#DA7955',
                '&:hover': {
                  backgroundColor: '#DA7955',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='automations.Automations.View.Edit' />
            </Button>
            <Button
              onClick={onDelete}
              variant='outlined'
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#8E8A84',
                'borderColor': '#E8E4DE',
                'backgroundColor': '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E8E4DE',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='automations.Automations.View.Delete' />
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            border: '1px solid',
            borderColor: '#E8E4DE',
            borderRadius: '12px',
            px: '14px',
            py: '12px',
            backgroundColor: '#FBFBFB',
          }}
        >
          <Stack
            sx={{
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              <Translate expression='automations.Automations.View.Description' />
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#1A1916',
                lineHeight: '150%',
              }}
            >
              {descriptionBlob}
            </Typography>
            <Box sx={{borderBottom: '1px solid #E8E4DE'}}></Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              <Translate expression='automations.Automations.TriggerConditions.Title' />
            </Typography>
            <Stack
              sx={{
                gap: '4px',
              }}
            >
              {
                isTriggerLocked ?
                  <Alert severity='warning'>
                    <Translate expression='automations.Automations.TriggerConditions.UnsupportedRuleMessage' />
                  </Alert> :
                  triggerConditionLines.map((line, index) => {
                    return (
                      <Stack
                        key={`${automation.taskRuleRn || automation.resourceType}-${index}`}
                        direction='row'
                        alignItems='flex-start'
                        sx={{
                          gap: '8px',
                        }}
                      >
                        <Box
                          sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '999px',
                            backgroundColor: '#DA7955',
                            mt: '6px',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#1A1916',
                            lineHeight: '150%',
                          }}
                        >
                          {line}
                        </Typography>
                      </Stack>
                    );
                  })
              }
            </Stack>
            <Box sx={{borderBottom: '1px solid #E8E4DE'}}></Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              <Translate expression='automations.Automations.View.Prompt' />
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#1A1916',
                lineHeight: '150%',
              }}
            >
              {prompt}
            </Typography>
            <Box sx={{borderBottom: '1px solid #E8E4DE'}}></Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#A8A49E',
                lineHeight: '100%',
                textTransform: 'uppercase',
              }}
            >
              <Translate expression='automations.Automations.View.Assignees' />
            </Typography>
            <Box
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#1A1916',
                lineHeight: '150%',
              }}
            >
              <Stack
                direction='row'
                sx={{
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {
                  assigneesLoading &&
                    assigneeRns.map((assigneeRn, index) => {
                      return (
                        <Skeleton
                          key={`${assigneeRn}-${index}`}
                          variant='rounded'
                          width={140}
                          height={32}
                        />
                      );
                    })
                }
                {
                  !assigneesLoading && assignees.length === 0 &&
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#A8A49E',
                        lineHeight: '150%',
                      }}
                    >
                      <Translate expression='automations.Automations.View.NoneAssigned' />
                    </Typography>
                }
                {
                  !assigneesLoading && !!assignees.length &&
                    assignees.map((assignee) => {
                      return (
                        <Chip
                          key={assignee.userRn}
                          label={
                            <Stack direction='row' gap={1} alignItems='center'>
                              <PersonOutline sx={{fontSize: 15}} />
                              <Typography variant='body2' color='text.secondary'>
                                <Translate expression='client.ClientDetail.Tasks.Chips.Assignee' />:
                              </Typography>
                              <Typography variant='body2'>
                                {`${assignee.firstName || ''} ${assignee.lastName || ''}`.trim() || assignee.userRn}
                              </Typography>
                            </Stack>
                          }
                          size='small'
                          sx={{
                            backgroundColor: '#F9F8F6',
                            border: '1px solid #E8E4DE',
                            px: '9px',
                            py: '4px',
                          }}
                        />
                      );
                    })
                }
              </Stack>
            </Box>
          </Stack>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <RecentInvocations
            tasks={automationInvocations}
            isLoading={automationInvocationsLoading}
          />
        </Box>
      </Stack>
    </Box>
  );
};

AutomationsView.propTypes = {
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  automation: PropTypes.object,
  assignees: PropTypes.array.isRequired,
  assigneesLoading: PropTypes.bool.isRequired,
  automationInvocations: PropTypes.array.isRequired,
  automationInvocationsLoading: PropTypes.bool.isRequired,
};

export default React.memo(AutomationsView);
